import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  Alert,
  useTheme,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useUser } from '../../../../context/UserContext';
import { useDealerGroup } from '../../../../context/DealerGroupContext';
import { useRole } from '../../../../context/RoleContext';
import SelectMAIQ from 'app/components/SelectMAIQ';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { headerSaga } from 'app/pages/Header/saga';
import { headerActions, reducer, sliceKey } from 'app/pages/Header/slice';
import { selectDealersList } from 'app/pages/Header/selectors';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  onClose,
  setRefreshKey,
}) => {
  const { addUser, user, setUser, error, setError, success, setSuccess } =
    useUser();
  const { dealerGroups, getAllDealerGroups } = useDealerGroup();
  const { roles, getAllRoles } = useRole();
  const theme = useTheme();
  const dispatch = useDispatch();

  // Inject header reducer and saga
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get dealers from header Redux state
  const dealersListDetails = useSelector(selectDealersList);
  const dealers = dealersListDetails.data?.body?.items || [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0); // 0 for All dealerships, 1 for Dealership Lists

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleRoleChange = (event: any) => {
    setUser(prev => ({ ...prev, roles: [event.target.value] }));
    setError('');
  };

  const handleDealerGroupChange = (event: any) => {
    const value = event.target.value;
    if (isDealershipExecutive && selectedTab === 0) {
      // When dealership_executive or ai_dealership_executive role and "All dealerships" tab is selected, store dealer ID
      setUser(prev => ({
        ...prev,
        dealer_id: value,
        dealer_group_id: '', // Clear dealer_group_id when dealer is selected
      }));
    } else {
      // For dealer groups or other roles, store dealer_group_id
      setUser(prev => ({
        ...prev,
        dealer_group_id: value,
        dealer_id: '', // Clear dealer_id when dealer_group_id is selected
      }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    // Reset both dealer_id and dealer_group_id when tab changes
    setUser(prev => ({
      ...prev,
      dealer_group_id: '',
      dealer_id: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare user data based on the selection
      let userData: any = { ...user };

      // For dealership_executive or ai_dealership_executive role, determine which field to send
      if (isDealershipExecutive) {
        if (selectedTab === 0 && user.dealer_id) {
          // "All dealerships" tab selected - send dealer ID
          userData = {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            password: user.password,
            roles: user.roles,
            dealer_id: user.dealer_id,
          };
        } else if (selectedTab === 1 && user.dealer_group_id) {
          // "Dealership Lists" tab selected - send dealer_group_id
          userData = {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            password: user.password,
            roles: user.roles,
            dealer_group_id: user.dealer_group_id,
          };
        }
      } else {
        // For other roles, always send dealer_group_id
        userData = {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          password: user.password,
          roles: user.roles,
          dealer_group_id: user.dealer_group_id,
        };
      }

      await addUser(userData, () => {
        setSuccess(true);
        setRefreshKey(prevKey => prevKey + 1);
        setSuccess(false);
        onClose(); //
        setUser({
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          roles: [],
          dealer_group_id: '',
          dealer_id: '',
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (roles.length === 0) getAllRoles();
    }
  }, [open]);

  useEffect(() => {
    if (user.roles.length > 0) {
      if (user.roles[0] === 'dealership_executive' || user.roles[0] === 'ai_dealership_executive') {
        // For dealership_executive or ai_dealership_executive role, fetch both dealers and dealer groups
        dispatch(headerActions.requestDealers());
        getAllDealerGroups(user.roles[0]);
      } else {
        getAllDealerGroups(user.roles[0]);
      }
    }
  }, [user.roles]);

  // Check if current role is dealership_executive or ai_dealership_executive
  const isDealershipExecutive =
    user.roles.length > 0 && (user.roles[0] === 'dealership_executive' || user.roles[0] === 'ai_dealership_executive');

  // Get the appropriate options based on selected tab
  const getDropdownOptions = () => {
    if (!isDealershipExecutive) {
      return dealerGroups;
    }

    if (selectedTab === 0) {
      // All dealerships tab - show dealers from header API
      return dealers;
    } else {
      // Dealership Lists tab - show dealer groups
      return dealerGroups;
    }
  };

  // Get the appropriate label based on selected tab
  const getDropdownLabel = () => {
    if (!isDealershipExecutive) {
      return 'Select dealer group';
    }

    if (selectedTab === 0) {
      return 'Select dealer';
    } else {
      return 'Select dealer group';
    }
  };

  // Get the appropriate render value based on selected tab
  const getRenderValue = () => {
    if (!isDealershipExecutive) {
      return 'name';
    }

    if (selectedTab === 0) {
      return 'name';
    } else {
      return 'name';
    }
  };

  // Get the appropriate selected value based on selected tab
  const getSelectedValue = () => {
    if (!isDealershipExecutive) {
      return '_id';
    }

    if (selectedTab === 0) {
      return '_id';
    } else {
      return '_id';
    }
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleAltIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Add New User
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          id="user-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              First Name *
            </Typography>
            <TextField
              fullWidth
              name="first_name"
              value={user.first_name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter first name"
              inputProps={{
                sx: { py: 1.5 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Last Name *
            </Typography>
            <TextField
              fullWidth
              name="last_name"
              value={user.last_name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter last name"
              inputProps={{
                sx: { py: 1.5 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Email *
            </Typography>
            <TextField
              fullWidth
              name="email"
              type="email"
              value={user.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter email address"
              inputProps={{
                sx: { py: 1.5 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Password *
            </Typography>
            <TextField
              fullWidth
              name="password"
              type="password"
              value={user.password}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter password"
              inputProps={{
                sx: { py: 1.5 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Role *
            </Typography>
            <Box
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '8px 14px',
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, 16px) scale(1)',
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                },
                '& .MuiFormControl-root': {
                  marginTop: 0,
                },
              }}
            >
              <SelectMAIQ
                label="Select role"
                value={
                  (user.roles && Array.isArray(user.roles)
                    ? user.roles[0]
                    : user.roles) || ''
                }
                options={roles}
                onChange={handleRoleChange}
                selectedValue="name"
                renderValue="name"
                disabled={isSubmitting}
              />
            </Box>
          </Box>

          {user.roles.length > 0 && isDealershipExecutive && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Dealership Selection *
              </Typography>

              {/* Tabs for dealership_executive or ai_dealership_executive role */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 40,
                      fontSize: '0.875rem',
                      flex: 1,
                    },
                    '& .MuiTabs-flexContainer': {
                      width: '100%',
                    },
                  }}
                >
                  <Tab label="All dealerships" />
                  <Tab label="Dealership Lists" />
                </Tabs>
              </Box>

              <Box
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                  '& .MuiInputLabel-root': {
                    transform: 'translate(14px, 16px) scale(1)',
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.75)',
                  },
                  '& .MuiFormControl-root': {
                    marginTop: 0,
                  },
                }}
              >
                <SelectMAIQ
                  key={`${selectedTab}-${getDropdownOptions().length}`}
                  label={getDropdownLabel()}
                  value={
                    selectedTab === 0
                      ? user.dealer_id || ''
                      : user.dealer_group_id || ''
                  }
                  options={getDropdownOptions()}
                  onChange={handleDealerGroupChange}
                  disabled={!user.roles[0] || isSubmitting}
                  renderValue={getRenderValue()}
                  selectedValue={getSelectedValue()}
                />
              </Box>
            </Box>
          )}

          {user.roles.length > 0 &&
            !isDealershipExecutive &&
            dealerGroups.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Dealer Group *
                </Typography>
                <Box
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      padding: '8px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      transform: 'translate(14px, 16px) scale(1)',
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)',
                    },
                    '& .MuiFormControl-root': {
                      marginTop: 0,
                    },
                  }}
                >
                  <SelectMAIQ
                    key={`${user.roles}-${dealerGroups}`}
                    label="Select dealer group"
                    value={user.dealer_group_id || ''}
                    options={dealerGroups}
                    onChange={handleDealerGroupChange}
                    disabled={!user.roles[0] || isSubmitting}
                    renderValue="name"
                    selectedValue="_id"
                  />
                </Box>
              </Box>
            )}
        </Box>

        {/* Footer with actions - fixed */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2,
          }}
        >
          <ButtonMAIQ
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonMAIQ>
          <ButtonMAIQ
            variant="contained"
            color="primary"
            type="submit"
            form="user-form"
            onClick={e => {
              handleSubmit(e as any);
            }}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
          >
            Create User
          </ButtonMAIQ>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddUserModal;
